import { PERMISSIONS } from '@/constants/permissions';

/**
 * Effective permission bitmask. Session accounts set `permissions` from the access JWT (see `mergeAccessTokenClaims`).
 * Otherwise merges `accountRoles` like cop-32 `checkPermission`.
 */
export function getEffectivePermissions(account) {
    if (!account) return 0n;
    if (typeof account.permissions === 'bigint') return account.permissions;
    if (typeof account.permissions === 'number') return BigInt(account.permissions);
    if (typeof account.role?.permissions === 'bigint') return account.role.permissions;
    if (typeof account.role?.permissions === 'number') return BigInt(account.role.permissions);
    
    if (Array.isArray(account.accountRoles)) {
        return account.accountRoles.reduce(
            (mask, ar) => {
                const p = ar.role?.permissions;
                const v = typeof p === 'bigint' ? p : BigInt(p ?? 0);
                return mask | v;
            },
            0n,
        );
    }
    return 0n;
}

/**
 * True if `mask` includes every required flag (same rule as backend `checkPermission` middleware).
 * @param {number|bigint} mask - Effective permission integer/BigInt from the account
 * @param {...(keyof typeof PERMISSIONS|number|bigint)} required - Permission keys or flags
 */
export function hasAllPermissions(mask, ...required) {
    const m = mask != null ? BigInt(mask) : 0n;
    for (const perm of required) {
        const permissionValue =
            typeof perm === 'string' ? PERMISSIONS[perm] : perm;
        if (permissionValue === undefined) return false;
        const v = BigInt(permissionValue);
        if ((m & v) !== v) return false;
    }
    return true;
}
